import { AVATAR_COLORS } from './constants';
import type { Employee } from './types';

export function getToken(): string {
  return localStorage.getItem('access_token') || localStorage.getItem('token') || '';
}

export function authHeaders(): Record<string, string> {
  return {
    Authorization: 'Bearer ' + getToken(),
    'Content-Type': 'application/json',
  };
}

export function employeeName(emp: Employee | null | undefined): string {
  if (!emp) return 'Unknown';
  const last = (emp.last_name || '').trim();
  const first = (emp.first_name || '').trim();
  if (last && first) return last + ', ' + first;
  if (emp.full_name) return emp.full_name;
  if (emp.name) return emp.name;
  return emp.email || 'Unnamed';
}

export function employeeInitials(emp: Employee | null | undefined): string {
  if (!emp) return '?';
  const f = (emp.first_name || '').trim();
  const l = (emp.last_name || '').trim();
  if (f && l) return (f[0] + l[0]).toUpperCase();
  if (emp.full_name) {
    const parts = emp.full_name.split(/\s+/);
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  if (emp.email) return emp.email.slice(0, 2).toUpperCase();
  return '?';
}

export function avatarColor(emp: Employee): string {
  const seed = (emp.id || emp.email || '').toString();
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export function formatCurrency(value: number | string | undefined, currency?: string): string {
  const n = parseFloat(String(value)) || 0;
  return n.toLocaleString('en-CA', {
    style: 'currency',
    currency: currency || 'CAD',
    minimumFractionDigits: 2,
  });
}

export function formatPhone(p: string | null | undefined): string | null {
  if (!p) return null;
  const digits = p.replace(/\D/g, '');
  if (digits.length === 10) return digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6);
  if (digits.length === 11 && digits[0] === '1') return digits.slice(1, 4) + '-' + digits.slice(4, 7) + '-' + digits.slice(7);
  return p;
}

export function formatPayRate(emp: Employee): string | null {
  if (emp.pay_type === 'hourly' && parseFloat(String(emp.hourly_rate))) {
    return formatCurrency(emp.hourly_rate, emp.currency) + '/hour';
  }
  if (emp.pay_type === 'salary' && parseFloat(String(emp.salary_amount))) {
    return formatCurrency(emp.salary_amount, emp.currency) + '/year';
  }
  return null;
}

export function getPayMethod(emp: Employee): string | null {
  if (emp.payment_method === 'direct_deposit') return 'Direct deposit';
  if (emp.payment_method === 'cheque' || emp.payment_method === 'paper_cheque' || emp.payment_method === 'check') {
    return 'Paper cheque';
  }
  return null;
}

export function getPhone(emp: Employee): string | null {
  return emp.phone || emp.mobile_phone || emp.home_phone || null;
}

export function getRole(emp: Employee): string | null {
  return emp.job_title || emp.position_title || null;
}

export function getVacation(emp: Employee): string | null {
  if (emp.vacation_balance != null && emp.vacation_balance !== '') {
    return (parseFloat(String(emp.vacation_balance)) || 0).toFixed(1) + ' hrs';
  }
  return null;
}

export function computeNextPayrollDue(): Date {
  const today = new Date();
  const next = new Date(today);
  if (today.getDate() < 15) {
    next.setDate(15);
  } else {
    next.setMonth(next.getMonth() + 1);
    next.setDate(0);
  }
  return next;
}

export function formatDueDate(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(date);
  due.setHours(0, 0, 0, 0);
  if (due.getTime() === today.getTime()) return 'Next payroll due today';
  const wd = due.toLocaleDateString('en-CA', { weekday: 'long' });
  const ds = due.toLocaleDateString('en-CA', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return 'Next payroll due ' + wd + ', ' + ds;
}